import { useEffect, useState } from "react";
import { format } from "date-fns";

import { getNextSaturday, getPreviousSunday } from "@utils/general";
import FlowbiteDatepickerInLine from "@components/DatepickerInLine";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import { IPlantSelect } from "@view/admin/HoursUpload";
import HeadlessComboboxInLine from "@components/ComboboxInLine";
import { generateSummary } from "@service/invoiceApiService";

interface IInvoiceSummaryProps {
  plants: IPlantSelect[];
}

export function InvoiceSummary({ plants }: IInvoiceSummaryProps): JSX.Element {
  const [sendingInvoice, setSendingInvoice] = useState<boolean>(false);
  const [start_date, setStartDate] = useState<Date | null>(
    getPreviousSunday(null, -1)
  );
  const [end_date, setEndDate] = useState<Date | null>(
    getNextSaturday(new Date(), -1)
  );
  const [selectedPlant, setSelectedPlant] = useState<IPlantSelect | null>(null);

  useEffect(() => {
    const selected = plants && plants.length > 0 ? plants[0] : null;
    setSelectedPlant(selected);
  }, [plants]);

  function handleStartDateChange(date: Date) {
    if (date.getDay() !== 0) {
      alert("Please select a Sunday.");
      return;
    }
    setStartDate(date);
  }

  function handleEndDateChange(date: Date | null) {
    if (!date) {
      setEndDate(null);
      return;
    }
    if (date.getDay() !== 6) {
      alert("Please select a Saturday.");
      return;
    }
    setEndDate(date);
  }

  async function handleSubmitInvoice() {
    try {
      if (!start_date || !end_date) {
        alert("Please select a valid start and end date.");
        return;
      }
      setSendingInvoice(true);
      const data = {
        start_date: format(start_date, "yyyy-MM-dd"),
        end_date: format(end_date, "yyyy-MM-dd"),
        plant_id: String(selectedPlant?.id),
      };
      const response = await generateSummary(data);
      const url = window.URL.createObjectURL(response.data as any);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        response.headers["content-disposition"]?.split("filename=")[1] ??
          "invoice.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSendingInvoice(false);
      alert("Invoice Summary successfully generated!");
    } catch (err) {
      setSendingInvoice(false);
      console.error("Invoice Summary error:", err);
      alert(`Invoice Summary failed. ${err}`);
    }
  }

  function handleChangePlant(value: IPlantSelect) {
    setSelectedPlant(value);
  }

  return (
    <>
      <div className="tw-flex tw-mb-2 tw-h-[42vh]">
        <div className="tw-flex tw-min-w-[380px] tw-flex-col">
          <h4 className="Subheader tw-mb-3">Invoice Summary</h4>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-ml-4 tw-mt-4">
            <div className="tw-w-8">Plant:</div>
            <HeadlessComboboxInLine
              value={plants ? plants[0] : ""}
              name="plant"
              options={plants}
              onChange={handleChangePlant}
              placeholder="Select a plant"
              errorMsg=""
              className="tw-w-[350px] tw-ml-2"
              data-testid={`${selectedPlant?.value}-combobox`}
              disabled={false}
            />
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-ml-4">
            <span className="tw-w-8">Start:</span>
            <FlowbiteDatepickerInLine
              name={undefined}
              value={start_date}
              handleOnChange={handleStartDateChange}
              handleOnBlur={undefined}
              className="tw-w-[350px] tw-ml-2"
            />
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-ml-4">
            <span className="tw-w-8">End:</span>
            <FlowbiteDatepickerInLine
              name={undefined}
              value={end_date}
              handleOnChange={handleEndDateChange}
              handleOnBlur={undefined}
              className="tw-w-[350px] tw-ml-2"
            />
          </div>
          <div className="tw-flex tw-justify-center tw-mt-6 tw-ml-14">
            <Button
              disabled={sendingInvoice}
              className="md:tw-w-[50%]"
              onClick={handleSubmitInvoice}
            >
              {sendingInvoice ? (
                <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
              ) : (
                "Generate Invoice"
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
